import {
  leadTimeConfigSchema,
  leadTimeConfigUpdateSchema,
} from '@snap-types/models/lead-time-config';
import {
  priceSegmentConfigUpdateSchema,
  priceSegmentConfigWithRelationsSchema,
} from '@snap-types/models/price-segment-config';
import { z } from 'zod/v4';

import {
  alphaColumns,
  capPriceSegmentationColumns,
  columns,
  fixedCustomerColumns,
  fixedDefaultColumns,
  fixedDiscountColumns,
  fixedMarketColumns,
  newPartColumns,
  newPartUserCostColumns,
  newPartUserPriceColumns,
  priceSegmentationColumns,
  priceSegmentationWithTargetGrossMarginColumns,
  rafFixedMarketColumns,
  rafTargetGrossMarginColumns,
  serviceChargeColumns,
  targetContributionMarginColumns,
  targetGrossMarginColumns,
  userImportColumns,
  userPriceColumns,
} from './columns';

export const priceStrategyConfigSchema = z.object(columns);

export type PriceStrategyConfig = z.output<typeof priceStrategyConfigSchema>;

const priceStrategyConfigWithRelationsBaseSchema = z.object({
  ...priceStrategyConfigSchema.shape,
  leadTimeConfig: leadTimeConfigSchema.nullish(),
});

export const priceStrategyConfigWithRelationsSchema = z.discriminatedUnion(
  'priceStrategyType',
  [
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...fixedCustomerColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...fixedDefaultColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...fixedDiscountColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...fixedMarketColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...targetGrossMarginColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...targetContributionMarginColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...priceSegmentationColumns.shape,
      // @NOTE(shawk): z.unknown() is a workaround for zod type inference issues
      priceSegmentConfigs: z
        .unknown()
        .pipe(z.array(priceSegmentConfigWithRelationsSchema))
        .default([]),
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...priceSegmentationWithTargetGrossMarginColumns.shape,
      // @NOTE(shawk): z.unknown() is a workaround for zod type inference issues
      priceSegmentConfigs: z
        .unknown()
        .pipe(z.array(priceSegmentConfigWithRelationsSchema))
        .default([]),
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...capPriceSegmentationColumns.shape,
      // @NOTE(shawk): z.unknown() is a workaround for zod type inference issues
      priceSegmentConfigs: z
        .unknown()
        .pipe(z.array(priceSegmentConfigWithRelationsSchema))
        .default([]),
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...newPartUserPriceColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...newPartUserCostColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...rafFixedMarketColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...rafTargetGrossMarginColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...newPartColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...userImportColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...userPriceColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...serviceChargeColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...alphaColumns.shape,
    }),
  ],
);

export type PriceStrategyConfigWithRelations = z.output<
  typeof priceStrategyConfigWithRelationsSchema
>;

const priceStrategyConfigUpdateBaseSchema = z.object({
  ...priceStrategyConfigSchema.pick({
    sortOrder: true,
    enabled: true,
    allowFallback: true,
  }).shape,
  leadTimeConfig: leadTimeConfigUpdateSchema.nullish(),
});

export const priceStrategyConfigUpdateSchema = z.discriminatedUnion(
  'priceStrategyType',
  [
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...fixedCustomerColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...fixedDefaultColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...fixedDiscountColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...fixedMarketColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...targetGrossMarginColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...targetContributionMarginColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...capPriceSegmentationColumns.shape,
      priceSegmentConfigs: z
        .array(priceSegmentConfigUpdateSchema)
        .min(1, { error: 'At least one price segment config is required' }),
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...priceSegmentationWithTargetGrossMarginColumns.shape,
      priceSegmentConfigs: z
        .array(priceSegmentConfigUpdateSchema)
        .min(1, { error: 'At least one price segment config is required' }),
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...priceSegmentationColumns.shape,
      priceSegmentConfigs: z
        .array(priceSegmentConfigUpdateSchema)
        .min(1, { error: 'At least one price segment config is required' }),
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...newPartColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...newPartUserPriceColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...newPartUserCostColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...rafFixedMarketColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...rafTargetGrossMarginColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...userImportColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...userPriceColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...serviceChargeColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigUpdateBaseSchema.shape,
      ...alphaColumns.shape,
    }),
  ],
);

export type PriceStrategyConfigUpdate = z.output<
  typeof priceStrategyConfigUpdateSchema
>;
